/* global t, runtime, mergeState, safeArray, num, getReportsModel, buildLedgerLines, getFixedAssetsModel, buildFixedAssetsModel, assetsReconRound, assetsReconMonthRange, assetsReconThreshold, assetsReconGroupAccounts, assetsReconRegister, assetsReconAcquisitions, assetsReconDepreciation, assetsReconDisposals, assetsReconDisposalLedger, assetsReconDirectPostings, assetsReconItems */
// EIENDELER — lesemodell for byråets eiendelsskjerm
//
// Eiendeler er det ene området der den eksterne saldoen ikke finnes: det er
// ingen bank som bekrefter at maskinen står der. Grunnlaget er appens eget
// anleggsregister, og avstemmingen er derfor en sammenlikning mellom to
// interne kilder — hovedboken og registeret — pluss kontrollene som fanger det
// som aldri kom inn i registeret i det hele tatt.
//
// Det siste er poenget med skjermen. En balanse som stemmer med registeret sier
// ingenting om kjøpet som ble kostnadsført i stedet for aktivert, om
// kreditføringen på anleggskontoen ingen avgang svarer for, eller om eiendelen
// som er fullt avskrevet og fortsatt i bruk. Saldoen er bestått med én gang;
// arbeidet ligger et annet sted.
//
// Modellen regner ikke ut avskrivninger. Registeret gjør det
// (`buildFixedAssetsModel`), etter saldometoden og per år, slik appens
// eiendelsside og årsoppgjør gjør det. Delene ligger i
// backend-assets-recon-parts.jsx; denne fila eier kontrollkontoene,
// sammenstillingen, datakildene og kontrollene.

// ------------------------------------------------------------------
// Kontrollkontoene i hovedboken
// ------------------------------------------------------------------
// Anleggskontoene fra saldogruppene, kontoene for akkumulerte avskrivninger, og
// kontoene byrået selv har lagt til eiendelsområdet. En importert kontoplan kan
// bruke andre numre, og da er det området som bestemmer.
const assetsReconControlAccounts = ({ state, range, areaAccounts, assets, lines, groupAccounts }) => {
  const reports = typeof getReportsModel === 'function'
    ? getReportsModel({
      year: range.year, months: [range.monthIndex, range.monthIndex],
      includeChart: false, includeResult: false,
    })
    : null;
  const trial = new Map();
  for (const group of safeArray(reports?.trial)) {
    for (const account of safeArray(group.accounts)) trial.set(String(account.nr || ''), account);
  }

  const roles = new Map();
  for (const code of groupAccounts.asset) roles.set(code, 'anleggskonto');
  for (const code of groupAccounts.accumulated) roles.set(code, 'akkumulert');
  for (const account of safeArray(areaAccounts)) {
    const code = String(account.accountId || '');
    if (code && !roles.has(code)) roles.set(code, null);
  }

  const movement = new Map();
  for (const line of safeArray(lines)) {
    const entry = movement.get(line.accountCode)
      || { additions: 0, disposals: 0, depreciation: 0 };
    const amount = num(line.amount);
    // Årsoppgjørets avskrivning er sin egen bevegelse. Slås den sammen med
    // avgangene, kan ikke en uregistrert avgang skilles fra en avskrivning.
    if (line.sourceType === 'yearEndEntry' && String(line.kind || '') === 'depreciation') entry.depreciation += -amount;
    else if (amount >= 0) entry.additions += amount;
    else entry.disposals += -amount;
    movement.set(line.accountCode, entry);
  }

  // Anleggskontoen i hovedboken star med ANSKAFFELSESKOST; de akkumulerte
  // avskrivningene ligger paa sin egen kontrakonto. Sammenliknes anleggskontoen
  // med registerets bokfoerte verdi, faar hver eneste periode en «differanse»
  // som bare er avskrivningene — og da sier tallet ingenting.
  const costByAccount = new Map();
  for (const asset of assets) {
    if (!asset.accountCode) continue;
    costByAccount.set(
      asset.accountCode,
      assetsReconRound((costByAccount.get(asset.accountCode) || 0) + asset.cost),
    );
  }

  const rows = [...roles.keys()].map((code) => {
    const role = roles.get(code);
    const account = trial.get(code) || null;
    const areaAccount = safeArray(areaAccounts).find((row) => String(row.accountId) === code) || null;
    const entry = movement.get(code) || { additions: 0, disposals: 0, depreciation: 0 };
    const closing = account ? assetsReconRound(account.ut)
      : areaAccount ? assetsReconRound(num(areaAccount.bookedBalance)) : 0;
    const opening = account ? assetsReconRound(account.inn)
      : areaAccount ? assetsReconRound(num(areaAccount.openingBalance)) : 0;
    // Kontoen for akkumulerte avskrivninger har ingen direkte motpart i
    // registeret: registerets akkumulerte avskrivning inkluderer aarets
    // beregnede avskrivning, som foerst bokfoeres i aarsoppgjoeret. Den staar
    // derfor som «ikke sammenliknet» og ikke som null kroner — forskjellen
    // mellom «ingen differanse» og «ingen sammenlikning» er hele poenget, og
    // aarets ubokfoerte avskrivning er i stedet en navngitt avstemmingslinje i
    // hovedavstemmingen.
    const registerValue = role === 'akkumulert' ? null
      : role === 'anleggskonto' || costByAccount.has(code)
        ? assetsReconRound(costByAccount.get(code) || 0)
        : null;
    const difference = registerValue === null ? null : assetsReconRound(closing - registerValue);
    const empty = !opening && !closing && !entry.additions && !entry.disposals && !entry.depreciation;
    return {
      code,
      name: account ? (account.navn || account.name || '') : (areaAccount?.accountName || ''),
      role,
      opening,
      additions: assetsReconRound(entry.additions),
      disposals: assetsReconRound(entry.disposals),
      depreciation: assetsReconRound(entry.depreciation),
      closing,
      registerValue,
      difference,
      status: empty && registerValue === null ? 'ikke_relevant'
        : difference === null ? 'ikke_sammenliknet'
          : Math.abs(difference) > 0.5 ? 'differanse' : 'stemmer',
      inArea: Boolean(areaAccount),
      breakdown: role === 'akkumulert' ? [] : assets
        .filter((asset) => asset.accountCode === code)
        .map((asset) => ({ id: asset.id, name: asset.name, amount: asset.cost, flags: asset.flags })),
    };
  }).sort((left, right) => left.code.localeCompare(right.code));

  return rows;
};

// ------------------------------------------------------------------
// Datakildene
// ------------------------------------------------------------------
// De to siste radene er kilder appen IKKE har. De står som «hentes ikke av
// appen» og ikke som «mangler», fordi det ikke er noe klienten kan sende inn i
// Plumme — de dokumenteres ved å laste opp dokumentasjon på området.
const assetsReconSources = ({ range, assets, acquisitions, depreciation, disposals, registerAvailable }) => {
  const missingDoc = assets.filter((asset) => asset.flags.includes('mangler_dok')).length;
  const undocumentedDisposals = disposals.filter((row) => row.kind === 'registrert' && !row.documented).length;
  return [
    {
      key: 'hovedbok',
      label: t('rca.src.ledger.label', 'Hovedbok og saldobalanse'),
      complete: true,
      detail: t('rca.src.ledger.detail', 'Til og med {date}', { date: range.to }),
    },
    {
      key: 'register',
      label: t('rca.src.register.label', 'Anleggsregister'),
      complete: registerAvailable,
      detail: registerAvailable
        ? (assets.length === 1
          ? t('rca.src.register.detailOne', '{count} eiendel i registeret', { count: assets.length })
          : t('rca.src.register.detailMany', '{count} eiendeler i registeret', { count: assets.length }))
        : t('rca.src.register.detailUnavailable', 'Registeret kunne ikke leses for {year}', { year: range.year }),
    },
    {
      key: 'kjopsbilag',
      label: t('rca.src.purchaseDocs.label', 'Kjøpsdokumentasjon for balanseførte eiendeler'),
      complete: missingDoc === 0,
      detail: missingDoc
        ? (missingDoc === 1
          ? t('rca.src.purchaseDocs.detailOne', '{count} eiendel mangler bilag', { count: missingDoc })
          : t('rca.src.purchaseDocs.detailMany', '{count} eiendeler mangler bilag', { count: missingDoc }))
        : t('rca.src.purchaseDocs.detailAll', 'Alle registrerte eiendeler har bilag'),
    },
    {
      key: 'avskrivningsgrunnlag',
      label: t('rca.src.depBasis.label', 'Avskrivningsgrunnlag fra registeret'),
      complete: depreciation.withoutPlan === 0,
      detail: depreciation.withoutPlan
        ? t('rca.src.depBasis.detailMissing', '{count} eiendeler uten beregnet avskrivning for {year}', { count: depreciation.withoutPlan, year: range.year })
        : t('rca.src.depBasis.detailOk', 'Registeret har avskrivning for {year} på alle eiendeler i drift', { year: range.year }),
    },
    {
      key: 'avgangsdokumentasjon',
      label: t('rca.src.disposalDocs.label', 'Dokumentasjon for avganger'),
      complete: undocumentedDisposals === 0,
      detail: undocumentedDisposals
        ? t('rca.src.disposalDocs.detailMissing', '{count} registrert avgang uten bilag', { count: undocumentedDisposals })
        : disposals.length
          ? t('rca.src.disposalDocs.detailOk', 'Registrerte avganger har bilag')
          : t('rca.src.disposalDocs.detailNone', 'Ingen avganger i perioden'),
    },
    {
      key: 'anskaffelser',
      label: t('rca.src.acquisitions.label', 'Leverandørbilag i perioden'),
      complete: true,
      detail: t('rca.src.acquisitions.detail', '{count} kjøp analysert mot aktiveringsgrensen', { count: acquisitions.length }),
    },
    // Eksistens og eierskap kan ikke leses av bokføringen. En fysisk kontroll og
    // en forsikringsoversikt er byråets egen dokumentasjon, og lastes opp på
    // området — appen henter dem ikke fra noe sted.
    {
      key: 'fysisk_kontroll',
      label: t('rca.src.physical.label', 'Dokumentasjon av fysisk kontroll'),
      complete: false,
      external: true,
      detail: t('rca.src.physical.detail', 'Bekrefter at eiendelene finnes. Lastes opp som dokumentasjon på området.'),
    },
    {
      key: 'forsikring',
      label: t('rca.src.insurance.label', 'Forsikrings- og panteoversikt'),
      complete: false,
      external: true,
      detail: t('rca.src.insurance.detail', 'Bekrefter eierskap og heftelser. Lastes opp som dokumentasjon på området.'),
    },
  ];
};

// ------------------------------------------------------------------
// De automatiske kontrollene
// ------------------------------------------------------------------
// Kontrollene krysser ikke av noe i den faglige sjekklista. De sier hva den som
// krysser av bør ha sett på, med tallet de fant.
const assetsReconChecks = ({ counts, controlAccounts, acquisitions, depreciation, disposalGain }) => [
  {
    key: 'register_mot_hovedbok',
    label: t('rca.check.registerVsLedger', 'Anleggsregisteret er sammenliknet med hovedboken'),
    value: controlAccounts.filter((row) => row.status === 'differanse').length,
  },
  {
    key: 'kontoer',
    label: t('rca.check.accounts', 'Alle anleggskontoer i hovedboken er inkludert'),
    value: controlAccounts.filter((row) => row.role === 'anleggskonto' && !row.inArea && Math.abs(row.closing) > 0.5).length,
  },
  { key: 'tilganger', label: t('rca.check.additions', 'Tilganger i perioden er analysert'), value: 0, info: t('rca.check.additionsInfo', '{count} kjøp', { count: acquisitions.length }) },
  { key: 'avganger', label: t('rca.check.disposals', 'Avganger er analysert'), value: counts.muligAvgang },
  {
    key: 'vederlag',
    label: t('rca.check.proceeds', 'Vederlag ved avgang er bokført'),
    // Per rad kan et vederlag bare knyttes til bokfoeringen naar INGENTING er
    // bokfoert paa salgssumskontoene. Er noe bokfoert, men ikke alt, deler
    // flere avganger konto — og da er totalen det eneste aerlige svaret.
    value: Math.abs(disposalGain.registerProceeds - disposalGain.bookedProceeds) > 0.5
      ? Math.max(1, counts.vederlagIkkeBokfort)
      : counts.vederlagIkkeBokfort,
  },
  {
    key: 'gevinst',
    label: t('rca.check.gain', 'Gevinst og tap ved avgang er beregnet'),
    value: counts.gevinstTilKontroll,
  },
  {
    key: 'avskrivninger',
    label: t('rca.check.depreciation', 'Bokført avskrivning er sammenliknet med registeret'),
    value: depreciation.deviatingGroups,
  },
  { key: 'direktefort', label: t('rca.check.directPostings', 'Direkteføringer på anleggskonto er identifisert'), value: counts.direkteforing },
  { key: 'aktivering', label: t('rca.check.capitalization', 'Større kostnadsførte kjøp er analysert'), value: counts.muligAktivering },
  { key: 'negativ', label: t('rca.check.negative', 'Negative bokførte verdier er kontrollert'), value: counts.negativVerdi },
  { key: 'fullt_avskrevet', label: t('rca.check.fullyDepreciated', 'Fullt avskrevne eiendeler er identifisert'), value: counts.fulltAvskrevet },
  { key: 'dokumentasjon', label: t('rca.check.documentation', 'Manglende kjøpsdokumentasjon er identifisert'), value: counts.manglerDok },
  { key: 'uten_plan', label: t('rca.check.noPlan', 'Eiendeler uten beregnet avskrivning er identifisert'), value: depreciation.withoutPlan },
];

// ------------------------------------------------------------------
// Hele eiendelsbildet for én periode
// ------------------------------------------------------------------

const buildAssetsReconciliationView = ({ periodId, accounts = [], state: suppliedState } = {}) => {
  const range = assetsReconMonthRange(periodId);
  const empty = {
    periodId, from: '', to: '', year: null, assets: [], items: [], controlAccounts: [], ledgerLines: [],
    acquisitions: [], disposals: [], directPostings: [], sources: [], checks: [], counts: {},
    main: null, depreciation: { rows: [], expected: 0, booked: 0, difference: 0, deviatingGroups: 0, withoutPlan: 0 },
    threshold: null, registerAvailable: false, registerEmpty: true,
  };
  if (!range) return empty;
  const state = suppliedState || mergeState(runtime.appState);

  // Uten et medsendt state er `getFixedAssetsModel` riktig: den cacher paa
  // appens eget state, og registeret bygges en gang per innlasting. Med et
  // medsendt state maa registeret bygges for nettopp det regnskapet, ellers
  // ville modellen lest ett regnskaps hovedbok mot et annets register.
  const model = suppliedState
    ? (typeof buildFixedAssetsModel === 'function' ? buildFixedAssetsModel({ year: range.year, state }) : null)
    : (typeof getFixedAssetsModel === 'function' ? getFixedAssetsModel({ year: range.year }) : null);
  const registerAvailable = Boolean(model);
  const assets = assetsReconRegister({ model, range });
  const groupAccounts = assetsReconGroupAccounts();

  // Kontoene linjene skal leses for: saldogruppenes egne, byråets tillegg til
  // området, og avskrivningskontoene — de siste ligger i resultatet og hører
  // ikke til området, men uten dem kan ikke en riktig beregnet avskrivning som
  // aldri ble bokført skilles fra en som ble det.
  const areaCodes = safeArray(accounts).map((account) => String(account.accountId || '')).filter(Boolean);
  const assetAccounts = [...new Set([...groupAccounts.asset, ...areaCodes])];
  const monthCodes = [...new Set([...assetAccounts, ...groupAccounts.accumulated])];
  const lines = monthCodes.length && typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.from, to: range.to, accountCodes: monthCodes, state })
    : [];
  // Avskrivningen bokføres i årsoppgjøret, ikke månedlig. Sammenlikningen må
  // derfor gjelde året til og med perioden — måneden alene ville sammenliknet
  // hele årets beregnede avskrivning med én måneds føringer.
  const depreciationLines = groupAccounts.depreciation.length && typeof buildLedgerLines === 'function'
    ? buildLedgerLines({ from: range.yearFrom, to: range.to, accountCodes: groupAccounts.depreciation, state })
    : [];

  const threshold = assetsReconThreshold({ country: state.settings?.country, date: range.to });
  const acquisitions = assetsReconAcquisitions({ state, range, threshold, assetAccounts });
  const depreciation = assetsReconDepreciation({ assets, lines: depreciationLines, range });
  const disposalLedger = assetsReconDisposalLedger({ state, range });
  const disposals = assetsReconDisposals({ assets, range, lines, assetAccounts, disposalLedger });
  const directPostings = assetsReconDirectPostings({ lines, assets, assetAccounts });
  const items = assetsReconItems({
    assets, acquisitions, depreciation, disposals, directPostings,
    periodLabel: window.avstPeriodLabel ? window.avstPeriodLabel(periodId) : periodId,
  });

  const controlAccounts = assetsReconControlAccounts({
    state, range, areaAccounts: accounts, assets, lines, groupAccounts,
  });

  // Registerets beregnede gevinst mot det bokfoeringen viser. Differansen er
  // som regel avskrivningen som ikke er bokfoert — motkontoen baerer da hele
  // kostprisen i stedet for bokfoert verdi.
  const disposalGain = {
    computed: assetsReconRound(disposals
      .filter((row) => row.gain !== null)
      .reduce((sum, row) => sum + row.gain, 0)),
    booked: disposalLedger.net,
    registerProceeds: assetsReconRound(disposals
      .filter((row) => row.kind === 'registrert')
      .reduce((sum, row) => sum + row.saleAmount, 0)),
    bookedProceeds: disposalLedger.proceeds,
  };

  const flagCount = (flag) => items.filter((item) => item.flags.includes(flag)).length;
  const counts = {
    flagged: items.length,
    muligAktivering: flagCount('mulig_aktivering'),
    direkteforing: flagCount('direkteforing'),
    manglerDok: flagCount('mangler_dok'),
    avskrDiff: flagCount('avskr_diff'),
    utenAvskrivning: flagCount('uten_avskrivning'),
    fulltAvskrevet: flagCount('fullt_avskrevet'),
    muligAvgang: flagCount('mulig_avgang'),
    avgangRegistrert: flagCount('avgang_registrert'),
    gevinstTilKontroll: flagCount('gevinst_til_kontroll'),
    vederlagIkkeBokfort: flagCount('vederlag_ikke_bokfort'),
    negativVerdi: flagCount('negativ_verdi'),
    accountDeviations: controlAccounts.filter((row) => row.status === 'differanse').length,
    nyIPerioden: assets.filter((asset) => asset.flags.includes('ny')).length,
  };

  // Hovedavstemmingen: bokfoert verdi i hovedboken mot bokfoert verdi i
  // registeret. Bokfoert verdi i hovedboken er anleggskontoene MINUS de
  // akkumulerte avskrivningene — begge sider maa vaere med, ellers
  // sammenliknes anskaffelseskost med bokfoert verdi.
  const compared = controlAccounts.filter((row) => row.role || row.inArea);
  const booked = assetsReconRound(compared.reduce((sum, row) => sum + row.closing, 0));
  const register = assetsReconRound(assets.reduce((sum, asset) => sum + asset.bookValue, 0));
  // Aarets beregnede avskrivning som ennaa ikke er bokfoert. Den er en KJENT
  // forskjell mellom de to kildene og ikke et avvik: avskrivningen bokfoeres i
  // aarsoppgjoeret. Uten linjen ville hver eneste periode foer aarsslutt vist
  // en uforklart differanse som alltid hadde samme aarsak.
  const notBookedDepreciation = assetsReconRound(depreciation.expected - depreciation.booked);
  const explained = assetsReconRound(safeArray(accounts).reduce(
    (sum, account) => sum + safeArray(account.explainedItems).reduce((inner, item) => inner + num(item?.amount), 0), 0,
  ));
  const difference = assetsReconRound(booked - register);
  const main = {
    booked,
    register,
    difference,
    notBookedDepreciation,
    explained,
    unexplained: assetsReconRound(difference - notBookedDepreciation - explained),
    comparedAccounts: compared.map((row) => row.code),
    cost: assetsReconRound(assets.reduce((sum, asset) => sum + asset.cost, 0)),
    accumulated: assetsReconRound(assets.reduce((sum, asset) => sum + asset.accumulated, 0)),
    activeAssets: assets.filter((asset) => asset.status === 'active').length,
    additions: counts.nyIPerioden,
    disposals: disposals.filter((row) => row.kind === 'registrert').length,
    yearDepreciation: depreciation.expected,
  };

  return {
    periodId,
    from: range.from,
    to: range.to,
    year: range.year,
    threshold,
    significance: window.ASSETS_RECON_SIGNIFICANCE || null,
    country: String(state.settings?.country || 'NO').toUpperCase(),
    registerAvailable,
    // Saldo 0 kr gir aldri automatisk «ikke relevant». Er registeret tomt OG
    // balansen null, er det fortsatt regnskapsføreren som må bekrefte at
    // klienten faktisk ikke har anleggsmidler — kontrollene under peker på det
    // som ville motsagt det.
    registerEmpty: assets.length === 0,
    balanceEmpty: Math.abs(booked) < 0.5,
    assets,
    acquisitions,
    depreciation,
    disposals,
    disposalLedger,
    disposalGain,
    directPostings,
    items,
    controlAccounts,
    ledgerLines: lines,
    main,
    counts,
    sources: assetsReconSources({
      range, assets, acquisitions, depreciation, disposals, registerAvailable,
    }),
    checks: assetsReconChecks({ counts, controlAccounts, acquisitions, depreciation, disposalGain }),
  };
};

Object.assign(window, {
  buildAssetsReconciliationView, assetsReconControlAccounts, assetsReconSources, assetsReconChecks,
});
